import { Inject, Injectable, Optional } from "@angular/core";

import { EnvMicroDocument } from "../config/env.micro.document";
import { DOCUMENT_ENV } from "../config/micros-env.tokens";

const FORBIDDEN_CHARACTERS = /[\u0000-\u0020\u007f-\u009f\s]/u;

@Injectable({ providedIn: "root" })
export class ChatMediaUrlPolicy {
  private readonly ownedObjectUrls = new Set<string>();
  private readonly documentOrigin: string | null;
  private readonly applicationOrigin: string | null;
  private readonly allowedOrigins: ReadonlySet<string>;

  constructor(
    @Optional() @Inject(DOCUMENT_ENV) documentEnv?: EnvMicroDocument,
  ) {
    this.applicationOrigin = exactOrigin(this.readApplicationOrigin());
    const configuredDocumentOrigin = exactConfiguredOrigin(documentEnv?.host);
    if (documentEnv?.host && !configuredDocumentOrigin) {
      throw new Error("Invalid Document media origin configuration");
    }
    const configuredMediaOrigins = (documentEnv?.mediaAllowedOrigins ?? []).map(
      (value) => {
        const origin = exactConfiguredOrigin(value);
        if (!origin)
          throw new Error("Invalid Document media origin configuration");
        return origin;
      },
    );
    const localApplicationOrigin = isLoopbackOrigin(this.applicationOrigin)
      ? this.applicationOrigin
      : null;
    this.documentOrigin = configuredDocumentOrigin ?? localApplicationOrigin;
    this.allowedOrigins = new Set(
      [
        configuredDocumentOrigin,
        localApplicationOrigin,
        ...configuredMediaOrigins,
      ].filter((origin): origin is string => !!origin),
    );
  }

  protected readApplicationOrigin(): string | null | undefined {
    return globalThis.location?.origin;
  }

  resolvePersisted(value: string | null | undefined): string | null {
    if (value?.startsWith("blob:")) return null;
    return this.resolveNetworkUrl(value, true);
  }

  resolveRenderable(value: string | null | undefined): string | null {
    if (value?.startsWith("blob:")) {
      return this.ownedObjectUrls.has(value) ? value : null;
    }
    return this.resolveNetworkUrl(value, true);
  }

  resolveAbsolute(value: string | null | undefined): string | null {
    if (!value || value.startsWith("/") || value.startsWith("blob:"))
      return null;
    return this.resolveNetworkUrl(value, false);
  }

  createObjectUrl(value: Blob): string | null {
    if (!globalThis.URL?.createObjectURL) return null;
    const url = globalThis.URL.createObjectURL(value);
    this.ownedObjectUrls.add(url);
    return url;
  }

  revokeObjectUrl(value: string | null | undefined): void {
    if (!value || !this.ownedObjectUrls.delete(value)) return;
    globalThis.URL?.revokeObjectURL?.(value);
  }

  private resolveNetworkUrl(
    value: string | null | undefined,
    allowRelativeDocumentPath: boolean,
  ): string | null {
    if (!value || value !== value.trim() || FORBIDDEN_CHARACTERS.test(value)) {
      return null;
    }
    if (value.startsWith("//")) return null;

    let parsed: URL;
    try {
      if (value.startsWith("/")) {
        if (!allowRelativeDocumentPath || !this.documentOrigin) return null;
        parsed = new URL(value, this.documentOrigin);
      } else {
        parsed = new URL(value);
      }
    } catch {
      return null;
    }

    if (parsed.username || parsed.password) return null;
    if (!this.allowedOrigins.has(parsed.origin)) return null;
    if (parsed.protocol === "https:") return parsed.href;
    if (parsed.protocol !== "http:") return null;
    if (isLoopback(parsed.hostname)) return parsed.href;
    return null;
  }
}

/**
 * Devuelve la URL con el path SIN percent-encoding, lista para viajar como
 * valor de un query param.
 *
 * Hace falta porque el valor que tenemos ya viene codificado y no debe
 * codificarse dos veces. La cadena real es:
 *
 * 1. klab-document-storage arma el reference con el nombre de archivo CRUDO
 *    (`ChatUploadApplication`: `referencePrefix + finalKey`), o sea con el
 *    espacio literal de "Captura de pantalla.png".
 * 2. klab-messaging-api-ms lo normaliza con `new URL(value).href`
 *    (`media-reference.validator`), y eso convierte ese espacio en `%20`. Esa
 *    es la forma que queda persistida y la que vuelve en el mensaje.
 * 3. Si acá se aplica `encodeURIComponent` sobre eso, el `%` se vuelve a
 *    codificar y sale `%2520`. El backend decodifica una sola vez, se queda con
 *    el texto literal `%20` y ya no coincide con la key de S3, que tiene el
 *    espacio de verdad.
 *
 * Decodificando el path antes de codificar, el servidor recibe exactamente el
 * valor crudo. No sirve mandar la URL sin codificar: un `&`, un `#` o un `+` en
 * el nombre romperían el query string.
 */
export function toRawMediaReference(value: string): string {
  let parsed: URL;
  try {
    parsed = new URL(value);
  } catch {
    return value;
  }

  let pathname: string;
  try {
    pathname = decodeURIComponent(parsed.pathname);
  } catch {
    // Secuencia de escape rota: mejor mandar el path tal cual que romper el envío.
    pathname = parsed.pathname;
  }

  return `${parsed.origin}${pathname}${parsed.search}${parsed.hash}`;
}

export function isOpaqueDocumentKey(
  value: string | null | undefined,
): value is string {
  if (!value || value !== value.trim() || FORBIDDEN_CHARACTERS.test(value)) {
    return false;
  }
  if (value.startsWith("/") || value.startsWith("//") || value.includes("\\")) {
    return false;
  }
  if (!/^[A-Za-z0-9][A-Za-z0-9._/-]{0,1023}$/.test(value)) return false;
  return !value.split("/").some((segment) => segment === "..");
}

function exactOrigin(value: string | null | undefined): string | null {
  if (!value || value !== value.trim() || FORBIDDEN_CHARACTERS.test(value)) {
    return null;
  }
  try {
    const parsed = new URL(value);
    if (parsed.username || parsed.password) return null;
    return parsed.origin === "null" ? null : parsed.origin;
  } catch {
    return null;
  }
}

function exactConfiguredOrigin(
  value: string | null | undefined,
): string | null {
  const origin = exactOrigin(value);
  if (!origin || (value !== origin && value !== `${origin}/`)) return null;
  return origin;
}

function isLoopback(hostname: string): boolean {
  return (
    hostname === "localhost" ||
    /^127(?:\.\d{1,3}){3}$/.test(hostname) ||
    hostname === "[::1]" ||
    hostname.endsWith(".localhost")
  );
}

function isLoopbackOrigin(origin: string | null): origin is string {
  if (!origin) return false;
  try {
    return isLoopback(new URL(origin).hostname);
  } catch {
    return false;
  }
}
